/**
 * © [2023] Conduent, Inc. All rights reserved.
 */

import { groupBy } from 'lodash';
import moment from 'moment';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/* ---------------- CONSTANTS ---------------- */

const contentFont = 10;
const pageMargins = [32, 100, 28.8, 25.92];

/* ---------------- FORMATTERS ---------------- */

const valueFormatter = (value = 0) => {
  if (value < 0) {
    return `($${Math.abs(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })})`;
  }
  return `$${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const numberFormatter = (value = 0, isUnit = false) =>
  Number(value).toLocaleString('en-US', {
    minimumFractionDigits: isUnit ? 4 : 0,
    maximumFractionDigits: isUnit ? 4 : 0,
  });

/* ---------------- TABLE HEADERS ---------------- */

const getHeaderColumns = (columns) =>
  columns.map(col => ({
    text: col,
    alignment: 'center',
    bold: true,
    fontSize: contentFont,
    border: [true, true, true, true],
  }));

/* ---------------- TOTAL CALCULATION ---------------- */



const calculateTotals = (data) =>
  data.reduce(
    (acc, item) => {
      acc.numOfClaims += Number(item.numOfClaims || 0);
      acc.totalBilled += Number(item.totalBilled || 0);
      acc.totalPaid += Number(item.totalPaid || 0);
      acc.totalCalculated += Number(item.totalCalculated || 0);
      acc.totalCalculatedPaid += Number(item.totalCalculatedPaid || 0);
      acc.totalRebate += Number(item.totalRebate || 0);
      acc.units += Number(item.units || 0);
      acc.ura += Number(item.ura || 0);
      return acc;
    },
    {
      numOfClaims: 0,
      totalBilled: 0,
      totalPaid: 0,
      totalCalculated: 0,
      totalCalculatedPaid: 0,
      totalRebate: 0,
      units: 0,
      ura: 0,
    }
  );

/* ---------------- TOTAL ROW ---------------- */

const getTotalsRow = (totals) => {
  const border = [true, true, true, true];
      const RedColor='#a81f00'


  return [
    [
      { text: 'Total:', colSpan: 2, alignment: 'right', bold: true, border },
      {},
      { text: numberFormatter(totals.numOfClaims),
        alignment: 'right',
        bold: true,
        border
      },
      { text: valueFormatter(totals.totalBilled), alignment: 'right', bold: true, border,
                  color: totals.totalBilled < 0 ? RedColor : 'black',

       },
      { text: valueFormatter(totals.totalPaid), alignment: 'right', bold: true, border,
                  color: totals.totalPaid < 0 ? RedColor : 'black',

       },
      { text: valueFormatter(totals.totalCalculated), alignment: 'right', bold: true, border,
                          color: totals.totalCalculated < 0 ? RedColor : 'black',

       },
      { text: valueFormatter(totals.totalCalculatedPaid),
         alignment: 'right',
          color: totals.totalCalculatedPaid < 0 ? RedColor : 'black',
          bold: true,
           border
           },
      { text: valueFormatter(totals.totalRebate), alignment: 'right', bold: true, border,
        color: totals.totalRebate < 0 ? RedColor : 'black',

       },
      { text: numberFormatter(totals.ura, true), alignment: 'right', bold: true, border,
        color: totals.ura < 0 ? RedColor : 'black',

       },
      { text: numberFormatter(totals.units, true), alignment: 'right', bold: true, border,
        color: totals.units < 0 ? RedColor : 'black',

       },
    ],
  ];
};

const sortByNdc = (a, b) =>
  String(a.ndc).localeCompare(String(b.ndc), 'en', { numeric: true });

/* ---------------- PDF BUILDER ---------------- */

const getPdfData = (providerListData, providerListTableHead, data) => {
  const content = [];
  const groupedProviders = groupBy(providerListData, item => item.providerId);
  const headers = providerListTableHead.map(h => h.headerName);

  Object.entries(groupedProviders).forEach(([providerId, records], index) => {
    const sortedRecords = [...records].sort(sortByNdc);
    const providerName = records?.[0]?.name || '';
    const RedColor = '#a81f00';

    const body = [];
    body.push(getHeaderColumns(headers));

    sortedRecords.forEach(drug => {
      body.push([
        { text: drug.ndc, alignment: 'center', fontSize: contentFont - 1 },
        { text: drug.drugName, alignment: 'left', fontSize: contentFont - 1 },
        { text: numberFormatter(drug.numOfClaims), alignment: 'right' },
        { text: valueFormatter(drug.totalBilled), alignment: 'right', color: drug.totalBilled < 0 ? RedColor : 'black' },
        { text: valueFormatter(drug.totalPaid), alignment: 'right', color: drug.totalPaid < 0 ? RedColor : 'black' },
        { text: valueFormatter(drug.totalCalculated), alignment: 'right', color: drug.totalCalculated < 0 ? RedColor : 'black' },
        { text: valueFormatter(drug.totalCalculatedPaid), alignment: 'right', color: drug.totalCalculatedPaid < 0 ? RedColor : 'black' },
        { text: valueFormatter(drug.totalRebate), alignment: 'right', color: drug.totalRebate < 0 ? RedColor : 'black' },
        { text: numberFormatter(drug.ura, true), alignment: 'right', color: drug.ura < 0 ? RedColor : 'black' },
        { text: numberFormatter(drug.units, true), alignment: 'right', color: drug.units < 0 ? RedColor : 'black' },
      ]);
    });

    // ✅ Totals after all records
    const providerTotals = calculateTotals(sortedRecords);
    body.push(...getTotalsRow(providerTotals));

    content.push(
      {
        stack: [
          {
            text: `Provider ID : ${providerId}${providerName ? ' - ' + providerName : ''}`,
            alignment: 'center',
            margin: [0, 0, 0, 10],
            bold: true,
            fontSize: 10
          },
          {
            table: {
              headerRows: 1,
              widths: [70, 165, 60, 80, 70, 70, 100, 100, 70, 70],
              body,
            }
          }
        ],
        pageBreak: index === Object.keys(groupedProviders).length - 1 ? undefined : 'after'
      }
    );
  });

  return {
  header: (currentPage, pageCount) => ({
  margin: [28.8, 20, 28.8, 25.92],
  table: {
    widths: ['*', '*', '*'],
    body: [
      [
        { text: `Requested By: ${data.User}`, alignment: 'left', fontSize: 10 },
        { text: data.reportName, alignment: 'center', bold: true, fontSize: 12 },
        { text: `Date Printed: ${data.prinDate}`, alignment: 'right', fontSize: 10 },
      ],
      [
        {
          text: `Provider Eligibility: ${data?.rebateProgramName?.providerSelectionValue || ''}`,
          alignment: 'left',
          fontSize: 10
        },
        {
          text: `${data?.rebateProgramName?.rebateProgramName?.name?.replace('|', '-') || ''}`,
          alignment: 'center',
          bold: true,
          fontSize: 10
        },
        {
          text: `Total Records: ${data.totalRecords || 0}`,
          alignment: 'right',
          fontSize: 10
        },
      ],
      [
        {
          text: `Provider ID Type: ${data?.rebateProgramName?.referenceDataSelectedValue?.split('|')[1]?.trim() || ''}`,
          alignment: 'left',
          fontSize: 10
        },
        { text: '', alignment: 'center' },
        { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', fontSize: 10 },
      ],
      [
        { text: `Quarter: ${data.quarter}`, alignment: 'left', fontSize: 10 },
        { text: '', alignment: 'center' },
        { text: '', alignment: 'right' },
      ],
    ],
  },
  layout: 'noBorders',
}),

    pageMargins,
    pageOrientation: 'landscape',
    pageSize: { width: 1020, height: 700 },
    content,
  };
};



/* ---------------- EXPORT FUNCTION ---------------- */

const providerClaimAllDetailsPDF = (
  providerListData,
  providerListTableHead,
  claimListData,
  userDefaults,
  getRebateProgramName,
  quarter,
  getQuarter,
  providerId,
  fileName,
  providerName,
  providerEligibility,
  providerSelectionValue,
  User,
  loggedInUser,
  displayObj
) => {
  const data = {
    requestedBy: userDefaults?.userId || loggedInUser,
    prinDate: moment().format('MM/DD/YYYY'),
    reportName: 'Provider Claim Listing Report',
    rebateProgramName: displayObj,
    quarter: getQuarter,
    providerId,
    providerIdName: providerName,
    providerSelectionValue,
    totalRecords: providerListData.length,
    fileName,
    User,
  };

  const pdfData = getPdfData(providerListData, providerListTableHead, data);
  pdfMake.createPdf(pdfData).download(`${data.fileName}.pdf`);
};

export default providerClaimAllDetailsPDF;
